import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, DollarSign, Gift, Building2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useEmployeeSummary, useBirthdaysThisMonth, useActiveEmployees } from '@/hooks/finance/useEmployees';
import PeopleLayout from './PeopleLayout';
import { format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { ptBR } from 'date-fns/locale';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';

const SALARY_VISIBLE_EMAIL = 'lcosta@divuslegalgroup.com';

export default function PeopleDashboard() {
  const { data: summary, isLoading: loadingSummary } = useEmployeeSummary();
  const { data: birthdays, isLoading: loadingBirthdays } = useBirthdaysThisMonth();
  const { data: activeEmployees, isLoading: loadingEmployees } = useActiveEmployees();
  const { data: currentUser } = useCurrentAppUser();
  const canSeeSalary = (currentUser?.email || '').toLowerCase() === SALARY_VISIBLE_EMAIL;

  // Department distribution from actual employee data
  const departmentDistribution = activeEmployees
    ? Object.entries(
        activeEmployees.reduce((acc, emp) => {
          const dept = emp.department || 'Sem Departamento';
          acc[dept] = (acc[dept] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      ).sort((a, b) => b[1] - a[1])
    : [];

  const totalEmployees = activeEmployees?.length || 0;

  const stats = [
    {
      title: 'Total de Colaboradores',
      value: summary?.count ?? 0,
      icon: Users,
      color: 'text-blue-600 bg-blue-100',
    },
    {
      title: 'Folha Mensal (USD)',
      value: canSeeSalary
        ? (summary?.totalUSD
          ? `$${summary.totalUSD.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
          : '$0')
        : '***',
      icon: DollarSign,
      color: 'text-green-600 bg-green-100',
    },
    {
      title: 'Aniversariantes do Mês',
      value: birthdays?.length ?? 0,
      icon: Gift,
      color: 'text-pink-600 bg-pink-100',
    },
    {
      title: 'Business Units',
      value: summary?.byBusinessUnit
        ? `${summary.byBusinessUnit.us} US · ${summary.byBusinessUnit.br} BR`
        : '0',
      icon: Building2,
      color: 'text-purple-600 bg-purple-100',
    },
  ];

  return (
    <PeopleLayout>
      <h1 className="text-2xl font-bold tracking-tight">Pessoas</h1>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <div className={`p-2 rounded-lg ${stat.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
              </CardHeader>
              <CardContent>
                {loadingSummary ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <p className="text-2xl font-bold">{stat.value}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Birthdays Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-pink-600" />
              Aniversariantes de {safeFormat(new Date(), 'MMMM', { locale: ptBR })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingBirthdays ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : !birthdays?.length ? (
              <p className="text-muted-foreground text-sm">Nenhum aniversariante este mês.</p>
            ) : (
              <ul className="space-y-3">
                {birthdays.map((emp) => (
                  <li key={emp.id} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{emp.name}</span>
                    <Badge variant="outline" className="text-pink-600 border-pink-200 bg-pink-50">
                      Dia {emp.day}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Department Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-purple-600" />
              Distribuição por Departamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingEmployees ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : departmentDistribution.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nenhum colaborador cadastrado.</p>
            ) : (
              <ul className="space-y-3">
                {departmentDistribution.map(([dept, count]) => {
                  const percentage = totalEmployees > 0 ? Math.round((count / totalEmployees) * 100) : 0;
                  return (
                    <li key={dept} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{dept}</span>
                        <span className="text-muted-foreground">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </PeopleLayout>
  );
}
